import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import axios from 'axios'
import qs from 'query-string'
import {
    AcceptRegulationsPayload,
    AcceptRegulationsResponse,
    AcceptanceListParams,
    AcceptanceListResponse,
    EmployeeAcceptanceItem,
    EmployeeNotAccepted,
    MyAcceptedRegulation,
    MyRegulationStatus,
    RegulationPayload,
    RegulationsConfig,
    RegulationsStats,
    TokenRegulationQuery,
} from '../types'

type PgBoolLike =
    | boolean
    | {
          Bool?: boolean
          Valid?: boolean
      }
    | null
    | undefined

type PgTextLike =
    | string
    | {
          String?: string
          Valid?: boolean
      }
    | null
    | undefined

type PgTimestampLike =
    | string
    | {
          Time?: string
          Valid?: boolean
      }
    | null
    | undefined

const normalizeBool = (value: PgBoolLike): boolean => {
    if (typeof value === 'boolean') return value
    if (!value || typeof value !== 'object') return false
    if ('Bool' in value && typeof value.Bool === 'boolean') return value.Bool
    if ('bool' in value && typeof (value as any).bool === 'boolean')
        return (value as any).bool
    if ('Valid' in value && value.Valid === false) return false
    if ('valid' in value && (value as any).valid === false) return false
    return false
}

const normalizeText = (value: PgTextLike): string => {
    if (typeof value === 'string') return value
    if (!value || typeof value !== 'object') return ''
    if ('String' in value && typeof value.String === 'string')
        return value.String
    if ('string' in value && typeof (value as any).string === 'string')
        return (value as any).string
    return ''
}

const normalizeTimestamp = (value: PgTimestampLike): string => {
    if (typeof value === 'string') return value
    if (!value || typeof value !== 'object') return ''
    if ('Time' in value && typeof value.Time === 'string') return value.Time
    if ('time' in value && typeof (value as any).time === 'string')
        return (value as any).time
    return ''
}

const normalizeRegulation = (raw: any): RegulationsConfig => ({
    ...raw,
    regulation_type: normalizeText(raw?.regulation_type),
    is_active: normalizeBool(raw?.is_active),
    is_mandatory: normalizeBool(raw?.is_mandatory),
    effective_date: normalizeTimestamp(raw?.effective_date),
    expired_date: normalizeTimestamp(raw?.expired_date) || null,
    created_at: normalizeTimestamp(raw?.created_at),
    updated_at: normalizeTimestamp(raw?.updated_at),
    regulation_summary: normalizeText(raw?.regulation_summary),
})

// Get active regulation (for displaying to user)
export const getActiveRegulation = async () => {
    const response = await datacentral.get<ResponseObject<RegulationsConfig>>(
        '/regulations/config'
    )
    const result = response.data?.result
    if (!result) return {} as RegulationsConfig
    return normalizeRegulation(result)
}

// Get active regulation in public employee-confirmation flow
// Uses raw axios (no auth interceptor) because this is a public/token-based endpoint
export const getActiveRegulationByToken = async ({
    token,
}: TokenRegulationQuery) => {
    const response = await axios.get<ResponseObject<RegulationsConfig>>(
        `${import.meta.env.APP_API_URL}/regulations/config/public`,
        {
            params: { token },
        }
    )
    const result = response.data?.result
    if (!result) return {} as RegulationsConfig
    return normalizeRegulation(result)
}

// Get my regulation status (check if needs acceptance)
export const getMyRegulationStatus = async (): Promise<
    ResponseObject<MyRegulationStatus>
> => {
    const response = await datacentral.get('/regulations/config/my-status')
    return response.data
}

// Get regulation versions accepted by current employee
export const getMyAcceptedRegulations = async (): Promise<
    ResponseArray<MyAcceptedRegulation>
> => {
    const response = await datacentral.get<ResponseArray<MyAcceptedRegulation>>(
        '/regulations/config/my-accepted'
    )
    return {
        ...response.data,
        results: (response.data?.results || []).map((item) => ({
            ...normalizeRegulation(item),
            acceptance_id: item.acceptance_id,
            employee_id: item.employee_id,
            accepted_at: normalizeTimestamp(item.accepted_at) || null,
            acceptance_created_at:
                normalizeTimestamp(item.acceptance_created_at) || null,
            acceptance_updated_at:
                normalizeTimestamp(item.acceptance_updated_at) || null,
        })),
    }
}

// Get regulation status in public employee-confirmation flow
// Uses raw axios (no auth interceptor) because this is a public/token-based endpoint
export const getMyRegulationStatusByToken = async ({
    token,
}: TokenRegulationQuery): Promise<ResponseObject<MyRegulationStatus>> => {
    const response = await axios.get(
        `${import.meta.env.APP_API_URL}/regulations/config/public/my-status`,
        {
            params: { token },
        }
    )
    return response.data
}

// Get regulation file URL in public employee-confirmation flow
export const getRegulationFileUrlByToken = async ({
    token,
}: TokenRegulationQuery): Promise<string> => {
    const response = await axios.get<ResponseObject<string>>(
        `${import.meta.env.APP_API_URL}/regulations/config/public/file-url`,
        {
            params: { token },
        }
    )
    return response.data?.result || ''
}

// Accept regulation
// Uses raw axios for public/token-based flow, datacentral for authenticated flow
export const acceptRegulations = async (
    payload: AcceptRegulationsPayload
): Promise<ResponseObject<AcceptRegulationsResponse>> => {
    if (payload.token) {
        const response = await axios.post(
            `${import.meta.env.APP_API_URL}/regulations/config/public/accept`,
            payload
        )
        return response.data
    }
    const response = await datacentral.post(
        '/regulations/config/accept',
        payload
    )
    return response.data
}

// Admin: Get all regulations list
export const getRegulationsList = async (payload?: {
    page?: number
    page_size?: number
    regulation_type?: string
}) => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get<ResponseArray<RegulationsConfig>>(
        `/regulations/config/list${queryString}`
    )
    const rawResults = response.data?.results || []
    const normalizedResults = rawResults.map(normalizeRegulation)

    // Debug helper (disabled): inspect regulations payload from backend
    // console.log('[Regulations Debug] getRegulationsList', {
    //     query: payload,
    //     rawResults,
    //     normalizedResults,
    // })
    // console.table(
    //     normalizedResults.map((item) => ({
    //         regulation_id: item.regulation_id,
    //         regulation_version: item.regulation_version,
    //         regulation_type: item.regulation_type,
    //         is_active: item.is_active,
    //         is_mandatory: item.is_mandatory,
    //     }))
    // )

    return normalizedResults
}

// Admin: Get acceptance statistics
export const getRegulationsStats = async () => {
    const response = await datacentral.get<ResponseObject<RegulationsStats>>(
        '/regulations/config/stats'
    )
    return response.data?.result || {}
}

// Admin: Get employees not accepted
export const getEmployeesNotAccepted = async (payload?: {
    page?: number
    page_size?: number
}): Promise<ResponseArray<EmployeeNotAccepted>> => {
    const queryString = payload ? `?${qs.stringify(payload)}` : ''
    const response = await datacentral.get(
        `/regulations/config/not-accepted${queryString}`
    )
    return response.data
}

// Admin: Unified acceptance list (all active non-collaborator employees + their status)
export const getRegulationAcceptanceList = async (
    params: AcceptanceListParams = {}
): Promise<AcceptanceListResponse> => {
    const query = qs.stringify(
        {
            status: params.status ?? 'all',
            department_id: params.department_id,
            search: params.search ? params.search.trim() : undefined,
            regulation_version: params.regulation_version || undefined,
            page: params.page ?? 1,
            page_size: params.page_size ?? 20,
        },
        { skipEmptyString: true, skipNull: true }
    )
    const response = await datacentral.get<AcceptanceListResponse>(
        `/regulations/config/acceptance-list?${query}`
    )
    const data = response.data
    const results: EmployeeAcceptanceItem[] = (data?.results || []).map(
        (r: any) => ({
            employee_id: r.employee_id,
            employee_code: normalizeText(r.employee_code) || null,
            employee_name: r.employee_name,
            employee_email: r.employee_email,
            department_name: normalizeText(r.department_name) || null,
            position_name: normalizeText(r.position_name) || null,
            employee_start_date:
                normalizeTimestamp(r.employee_start_date) || null,
            regulations_accepted_version:
                normalizeText(r.regulations_accepted_version) || null,
            regulations_accepted_at:
                normalizeTimestamp(r.regulations_accepted_at) || null,
            acceptance_status: r.acceptance_status,
        })
    )
    return { ...data, results }
}

// Admin: Create new regulation
export const createRegulation = async (
    payload: RegulationPayload
): Promise<ResponseObject<RegulationsConfig>> => {
    const response = await datacentral.post('/regulations/config', payload)
    return response.data
}

// Admin: Update regulation
export const updateRegulation = async (
    regulationId: number,
    payload: Partial<RegulationPayload>
): Promise<ResponseObject<RegulationsConfig>> => {
    const response = await datacentral.put(
        `/regulations/config/${regulationId}`,
        payload
    )
    return response.data
}

// Admin: Activate regulation
export const activateRegulation = async (
    regulationId: number
): Promise<ResponseObject<RegulationsConfig>> => {
    // Debug helper (disabled)
    // console.log('[Regulations Debug] activateRegulation:request', {
    //     regulationId,
    // })
    const response = await datacentral.put(
        `/regulations/config/${regulationId}/activate`
    )
    // Debug helper (disabled)
    // console.log('[Regulations Debug] activateRegulation:response', {
    //     regulationId,
    //     response: response.data,
    // })
    return response.data
}

// Admin: Delete regulation
export const deleteRegulation = async (
    regulationId: number
): Promise<ResponseObject<null>> => {
    const response = await datacentral.delete(
        `/regulations/config/${regulationId}`
    )
    return response.data
}

// Admin: Get regulation by ID
export const getRegulationById = async (
    regulationId: number
): Promise<ResponseObject<RegulationsConfig>> => {
    const response = await datacentral.get(
        `/regulations/config/${regulationId}`
    )
    if (response.data?.result) {
        response.data.result = normalizeRegulation(response.data.result)
    }
    return response.data
}
